import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  ArrowsClockwise,
  Clock,
  CheckCircle,
  XCircle,
  StopCircle,
  ArrowSquareOut,
  VideoCamera,
  Image,
  Globe,
} from '@phosphor-icons/react';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'X' },
];

const PLATFORM_LABELS = {
  tiktok: 'TikTok',
  youtube: 'YouTube',
  instagram: 'Instagram',
  facebook: 'Facebook',
  twitter: 'X',
};

const STATUS = {
  PROCESSING: {
    label: 'Processing',
    cls: 'text-amber-300 border-amber-400/30 bg-amber-400/5',
  },
  PUBLISHED: {
    label: 'Published',
    cls: 'text-emerald-300 border-emerald-400/30 bg-emerald-400/5',
  },
  DRAFT_READY: {
    label: 'Draft ready',
    cls: 'text-sky-300 border-sky-400/30 bg-sky-400/5',
  },
  FAILED: {
    label: 'Failed',
    cls: 'text-red-300 border-red-400/30 bg-red-400/5',
  },
  CANCELLED: {
    label: 'Cancelled',
    cls: 'text-zinc-400 border-zinc-700 bg-zinc-900',
  },
};

const platformIcon = (platform) => {
  if (platform === 'youtube' || platform === 'tiktok') return VideoCamera;
  if (platform === 'instagram' || platform === 'facebook' || platform === 'twitter') {
    return Image;
  }
  return Globe;
};

export default function PublishingHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/social/history');
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load publishing history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [load]);

  const refreshTikTok = async (historyId) => {
    setBusy(`refresh:${historyId}`);
    try {
      await api.post(`/social/tiktok/history/${historyId}/refresh`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'TikTok status refresh failed');
    } finally {
      setBusy('');
    }
  };

  const cancelTikTok = async (historyId) => {
    setBusy(`cancel:${historyId}`);
    try {
      const { data } = await api.post(`/social/tiktok/history/${historyId}/cancel`);
      if (data.already_final) {
        toast.message('TikTok operation had already reached a final state');
      } else {
        toast.success('TikTok upload cancelled');
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'TikTok cancellation failed');
    } finally {
      setBusy('');
    }
  };

  const visibleItems = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter(item => item.platform === filter);
  }, [items, filter]);

  const counts = useMemo(() => {
    const out = { all: items.length };
    for (const item of items) {
      out[item.platform] = (out[item.platform] || 0) + 1;
    }
    return out;
  }, [items]);

  const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1200px]">
      <header className="mb-8">
        <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">
          Social Publishing
        </div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold mt-1">
          Publishing history.
        </h1>
        <p className="text-zinc-400 mt-2 text-sm max-w-2xl">
          Track everything published from TuneAmplifier across your connected social platforms.
        </p>
      </header>

      <div className="flex flex-wrap gap-2 mb-6">
        {FILTERS.map((item) => {
          const active = filter === item.value;
          const count = counts[item.value] || 0;

          return (
            <button
              key={item.value}
              type="button"
              onClick={() => setFilter(item.value)}
              className={[
                'px-3 py-2 rounded-lg border text-xs font-mono uppercase tracking-wide transition-colors',
                active
                  ? 'border-[#ccff00]/40 bg-[#ccff00]/5 text-[#ccff00]'
                  : 'border-zinc-800 bg-zinc-950/40 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700',
              ].join(' ')}
            >
              {item.label}
              <span className="ml-2 text-zinc-600">{count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading publishing history…</div>
      ) : visibleItems.length === 0 ? (
        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/40 p-10 text-center">
          <Globe size={32} className="mx-auto text-zinc-600" />
          <div className="font-display text-lg mt-3">
            {filter === 'all' ? 'No publishing history yet.' : `No ${PLATFORM_LABELS[filter]} posts yet.`}
          </div>
          <p className="text-sm text-zinc-500 mt-1">
            Publications made from TuneAmplifier will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleItems.map((item) => {
            const meta = STATUS[item.status] || {
              label: item.status || 'Unknown',
              cls: 'text-zinc-300 border-zinc-700 bg-zinc-900',
            };
            const PlatformIcon = platformIcon(item.platform);
            const processingTikTok =
              item.platform === 'tiktok' && item.status === 'PROCESSING';

            return (
              <div
                key={item.history_id}
                className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-5"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-zinc-700 bg-zinc-900 text-zinc-200 text-[11px] font-mono uppercase">
                        <PlatformIcon size={13} />
                        {PLATFORM_LABELS[item.platform] || item.platform}
                      </span>

                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] font-mono uppercase ${meta.cls}`}
                      >
                        {item.status === 'PUBLISHED' ? <CheckCircle size={13} /> :
                         item.status === 'FAILED' ? <XCircle size={13} /> :
                         item.status === 'PROCESSING' ? <Clock size={13} /> :
                         <VideoCamera size={13} />}
                        {meta.label}
                      </span>

                      <span className="text-xs text-zinc-600">
                        {formatDate(item.created_at)}
                      </span>

                      {item.platform === 'youtube' && item.platform_meta?.youtube_format && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full border border-red-500/30 bg-red-500/5 text-red-300 text-[11px] font-mono uppercase">
                          {item.platform_meta.youtube_format === 'short'
                            ? 'Short / Promo clip'
                            : 'Full song / Video'}
                        </span>
                      )}
                    </div>

                    <div className="mt-3 text-sm text-zinc-200 whitespace-pre-wrap break-words">
                      {item.content || 'Untitled publication'}
                    </div>

                    {item.external_id && (
                      <div className="mt-2 text-[11px] font-mono text-zinc-600 break-all">
                        ID: {item.external_id}
                      </div>
                    )}

                    {item.platform_meta?.error && (
                      <div className="mt-2 text-xs text-red-300 break-words">
                        {item.platform_meta.error}
                      </div>
                    )}

                    {item.media_url && (
                      <div className="mt-2 text-[11px] font-mono text-zinc-600 break-all">
                        Media: {item.media_url}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 shrink-0">
                    {processingTikTok && (
                      <>
                        <Button
                          variant="outline"
                          className="border-zinc-700"
                          onClick={() => refreshTikTok(item.external_id)}
                          disabled={busy === `refresh:${item.external_id}`}
                        >
                          <ArrowsClockwise size={15} className="mr-2" />
                          Refresh
                        </Button>

                        <Button
                          variant="outline"
                          className="border-amber-400/40 text-amber-200 hover:bg-amber-400/10"
                          onClick={() => cancelTikTok(item.external_id)}
                          disabled={busy === `cancel:${item.external_id}`}
                        >
                          <StopCircle size={15} className="mr-2" />
                          Cancel
                        </Button>
                      </>
                    )}

                    {item.external_url && (
                      <Button
                        variant="outline"
                        className="border-zinc-700"
                        onClick={() =>
                          window.open(
                            item.external_url,
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }
                      >
                        <ArrowSquareOut size={15} className="mr-2" />
                        Open {PLATFORM_LABELS[item.platform] || 'post'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
